/**
 * Baut die Rümpfe von `POST /tickets` und `PATCH /tickets/{id}` aus dem
 * Formularzustand des Sheets `task-sheet` — ein Modul für beide, weil beide
 * dieselben Felder in derselben Reihenfolge tragen (Spec 06a, „Felder in
 * Reihenfolge").
 *
 * `PATCH` trägt **nur die geänderten Felder**, nie den kompletten
 * Formularzustand (dieselbe Begründung wie `features/contracts/
 * contract-patch.ts`, K3: ein unverändertes Feld soll keine Regel auslösen,
 * die es gar nicht betrifft). Ein geleertes Feld wird direkt als `null`
 * eingetragen — „Direkt `null` statt `clear`" funktioniert im Web für jedes
 * leerbare Feld (Spec 02a, „Felder leeren"), `clear` ist die iOS-Ergänzung.
 * **Ausnahme `due_on`** (Spec 45): Pflichtfeld, nie `null`, nie geleert.
 *
 * Reine Funktionen, eigenes Modul wie `contract-patch.ts` — leichter zu
 * testen als eingebettet in `TaskSheet.tsx`.
 */

import { parseCents } from '@/lib/money'
import type { TicketCreate, TicketPriority, TicketStatus, TicketUpdate } from '@/features/tasks/api'

export interface TaskFormValues {
  readonly title: string
  readonly body: string
  /** Bleibt `string`, nicht `TicketPriority` — dieselbe Abwägung wie
   * `form.reason` in `ContractTermsSheet.tsx`: Die Optionen kommen als
   * `{value,label}` vom Server (`TaskPriorityField`), der literale Enum-Typ
   * gilt erst am Rand, wo der Rumpf entsteht (unten). */
  readonly priority: string
  /** `''` heißt „niemand zugewiesen". */
  readonly assigneeId: string
  /** Pflicht (Spec 45) — `''` ist nur der Zustand vor der Eingabe; `TaskSheet`
   * sendet dann nicht. */
  readonly dueOn: string
  /** Dezimal-Eingabe wie bei `lib/money.ts::parseCents`, `''` heißt „keine
   * Kosten". */
  readonly cost: string
  /** `''` heißt „keine Kostenstelle". */
  readonly costCenterId: string
  /** Nur beim Ändern gezeigt (Spec: „Beim Ändern zusätzlich ganz oben:
   * Status") — beim Anlegen bleibt der Wert unbenutzt, `TicketCreate` kennt
   * kein `status`-Feld (eine neue Aufgabe ist immer `open`). Ebenfalls
   * `string` statt `TicketStatus`, siehe `priority` oben. */
  readonly status: string
}

export function buildTaskCreateBody(subjectId: string, form: TaskFormValues): TicketCreate {
  const body = form.body.trim()

  return {
    subject_id: subjectId,
    title: form.title.trim(),
    body: body === '' ? null : body,
    priority: form.priority as TicketPriority,
    assignee_id: form.assigneeId === '' ? null : form.assigneeId,
    due_on: form.dueOn,
    cost_cents: parseCents(form.cost),
    cost_center_id: form.costCenterId === '' ? null : form.costCenterId,
  }
}

export function buildTaskPatchBody(form: TaskFormValues, initial: TaskFormValues): TicketUpdate {
  const body: TicketUpdate = {}

  const title = form.title.trim()
  if (title !== initial.title.trim()) body.title = title

  const bodyText = form.body.trim()
  if (bodyText !== initial.body.trim()) body.body = bodyText === '' ? null : bodyText

  if (form.priority !== initial.priority) body.priority = form.priority as TicketPriority
  if (form.assigneeId !== initial.assigneeId) body.assignee_id = form.assigneeId === '' ? null : form.assigneeId
  // Kein `null` mehr (Spec 45, Fachregel 2: 422) — ein leeres Feld sendet nichts.
  if (form.dueOn !== initial.dueOn && form.dueOn !== '') body.due_on = form.dueOn
  if (form.cost !== initial.cost) body.cost_cents = parseCents(form.cost)
  if (form.costCenterId !== initial.costCenterId) {
    body.cost_center_id = form.costCenterId === '' ? null : form.costCenterId
  }
  if (form.status !== initial.status) body.status = form.status as TicketStatus

  return body
}
